import React, { useState } from 'react';
import { useForceLightTheme } from '../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: ''
  });

  const reqs = [
    { label: 'At least 8 characters', met: formData.password.length >= 8 },
    { label: 'Contains a number', met: /\d/.test(formData.password) },
    { label: 'Passwords match', met: formData.password === formData.confirmPassword && formData.confirmPassword.length > 0 }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await register(formData.username, formData.email, formData.password);
      toast.success("Account created! Let's set up your profile.");

      // Skip splash/onboarding — profile setup IS the onboarding
      localStorage.setItem('club_onboarding_done', 'true');
      // Don't dismiss push prompt — let it show after profile setup

      navigate('/profile/setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed.');
    } finally { setLoading(false); }
  };

  const inputCls = "h-12 rounded-lg border bg-white/80 focus:bg-white transition-colors";
  const inputStyle = { borderColor: 'var(--fairway-sand)', color: '#000', WebkitTextFillColor: '#000' };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ background: 'var(--club-cream)' }} data-testid="register-page">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/logo-the-club.png" alt="The Club" className="h-24 w-auto mb-6" data-testid="register-logo" />
          <h1 className="font-serif text-3xl font-semibold tracking-tight" style={{ color: 'var(--deep-forest)' }}>Join The Club</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--warm-gray)' }}>Create your account to get started</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Username</Label>
            <Input placeholder="Choose a username" value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} required className={inputCls} style={inputStyle} data-testid="username-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Email</Label>
            <Input type="email" placeholder="Enter your email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required className={inputCls} style={inputStyle} data-testid="email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Password</Label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required className={`${inputCls} pr-10`} style={inputStyle} data-testid="password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-70 transition-opacity">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Confirm Password</Label>
            <Input type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} required className={inputCls} style={inputStyle} data-testid="confirm-password-input" />
          </div>

          {/* Requirements */}
          <div className="space-y-1.5 py-1">
            {reqs.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${r.met ? 'bg-[var(--club-green)] text-white' : 'bg-[var(--stone)]'}`}>
                  {r.met && <Check className="w-3 h-3" />}
                </div>
                <span style={{ color: r.met ? 'var(--deep-forest)' : 'var(--warm-gray)' }}>{r.label}</span>
              </div>
            ))}
          </div>

          <Button type="submit" className="w-full h-12 rounded-lg font-semibold text-sm tracking-wide btn-press" style={{ background: 'var(--club-green)', color: '#fff' }} disabled={loading || !reqs.every(r => r.met)} data-testid="register-submit-btn">
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-8">
          <div className="flex-1 h-px" style={{ background: 'var(--fairway-sand)' }} />
          <span className="text-xs" style={{ color: 'var(--soft-taupe)' }}>or</span>
          <div className="flex-1 h-px" style={{ background: 'var(--fairway-sand)' }} />
        </div>

        <p className="text-center text-sm" style={{ color: 'var(--warm-gray)' }}>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--club-green)' }} data-testid="login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
